import React, { Component } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { Link } from 'react-router-dom'
import Header from '../../Component/Header'
import Menu from '../../Component/Menu'
import Footer from '../../Component/Footer'
import NewReportService from '../NewReportService'
import NewJournalService from '../NewJournalService'
import withRouter from '../../withRouter'
import Credentials, { currentTenant, thousandsdisplay, userid, financialyearstart, financialyearend, currency, clientname, getFinancialYearLabel } from '../../Common/Credentials'
import { NumericFormat } from 'react-number-format'
import { Autocomplete } from '@mui/material'
import TextField from '@mui/material/TextField'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

/**
 * Lightweight Schedule III Profit & Loss report
 * - Uses the same data source as existing P&L components (retrivePLLedgers)
 * - Maps ledger groups into common Schedule III buckets for display
 */
class ProfitAndLossScheduleIII extends Component {
  constructor(props) {
    super(props)
    this.state = {
      rows: [],
      loading: true,
      fyfrom: null,
      fyto: null,
      fylist: [],
      fySelected: null,
      dirtaxpaid: [],
      inputindirecttax: [],
    }
  }

  componentDidMount() {
    this.setState({ loading: true })
    NewJournalService.getFinancialYears(currentTenant).then((response) => {
      this.setState({ fylist: response.data }, () => { this.fyyears(); this.populateDefaultYear() })
    }).catch(() => this.populateDefaultYear())
  }

  fyyears() {
    const def = { from: financialyearstart, to: financialyearend };
    const rawList = (this.state.fylist || []).concat(def);
    const uniqueList = [];
    const seen = new Set();
    rawList.forEach((item) => {
      if (!item || !item.from || !item.to) return;
      const lbl = getFinancialYearLabel(item.from, item.to);
      if (!seen.has(lbl)) {
        seen.add(lbl);
        uniqueList.push(item);
      }
    });
    this.setState({ fylist: uniqueList });
  }

  populateDefaultYear() {
    // choose default FY; if URL contains fy param use it
    const params = (this.props && this.props.location) ? new URLSearchParams(this.props.location.search) : new URLSearchParams(window.location.search)
    const fyParam = params.get('fy')
    let fy
    if (fyParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(fyParam))
        const from = decoded.from
        const to = decoded.to
        fy = (this.state.fylist || []).find(f => String(f.from) === String(from) && String(f.to) === String(to))
        if (!fy) {
          fy = { from: from, to: to }
        }
      } catch (e) {
        // Legacy / malformed fy param handling
        const list = this.state.fylist || []
        // Try exact concatenation match: option.from + '-' + option.to equals fyParam
        let found = list.find(opt => (`${String(opt.from)}-${String(opt.to)}`) === fyParam)
        if (!found) {
          // Try inclusion match: fyParam contains both opt.from and opt.to
          found = list.find(opt => fyParam.indexOf(String(opt.from)) !== -1 && fyParam.indexOf(String(opt.to)) !== -1)
        }
        if (!found) {
          // Try year-only match e.g. '2024-2025' or '2024'
          found = list.find(opt => {
            const fromYear = String(opt.from).substring(0,4)
            const toYear = String(opt.to).substring(0,4)
            return fyParam.indexOf(fromYear) !== -1 && fyParam.indexOf(toYear) !== -1
          })
        }
        if (found) {
          fy = found
        } else {
          // fallback: attempt splitting on first hyphen into two parts
          const parts = fyParam.split('-')
          if (parts.length >= 2) {
            const from = parts[0]
            const to = parts.slice(1).join('-')
            fy = list.find(f => String(f.from) === String(from) && String(f.to) === String(to)) || { from, to }
          }
        }
      }
    } else {
      fy = (this.state.fylist && this.state.fylist.length>0) ? this.state.fylist[0] : { from: financialyearstart, to: financialyearend }
    }
    // prefer the exact object instance from fylist if available to satisfy Autocomplete
    const match = (this.state.fylist || []).find(f => String(f.from) === String(fy.from) && String(f.to) === String(fy.to))
    this.setState({ fySelected: match || fy }, () => this.onFySelect(fy))
  }

  onFySelect = (value) => {
    let year
    if (!value) return
    if (value.from !== financialyearstart) {
      year = { from: value.from, to: value.to, tenantid: currentTenant }
      this.setState({ fyfrom: value.from, fyto: value.to })
    } else {
      year = { from: 'Cur', to: 'year', tenantid: currentTenant }
      this.setState({ fyfrom: financialyearstart, fyto: financialyearend })
    }

    // fetch direct and indirect tax details
    NewReportService.getDirtaxPaid(currentTenant).then((res) => {
      this.setState({ dirtaxpaid: res.data || [] })
    }).catch(() => this.setState({ dirtaxpaid: [] }))

    NewReportService.getInputIndirecttax(currentTenant).then((res) => {
      this.setState({ inputindirecttax: res.data || [] })
    }).catch(() => this.setState({ inputindirecttax: [] }))

    // fetch the ledger totals for the selected year
    this.setState({ loading: true })
    NewReportService.retrivePLLedgers(year).then(res => {
      this.setState({ rows: res.data || [], loading: false })
      // persist selected FY in URL as fy=from-to
      try {
        // persist FY as encoded JSON to avoid issues with hyphens in ISO dates
        const fyObj = { from: value.from, to: value.to }
        const fyParam = encodeURIComponent(JSON.stringify(fyObj))
        if (this.props && this.props.navigate) {
          const pathname = this.props.location ? this.props.location.pathname : window.location.pathname
          this.props.navigate(`${pathname}?fy=${fyParam}`, { replace: true })
        } else {
          const url = new URL(window.location.href)
          url.searchParams.set('fy', fyParam)
          window.history.replaceState({}, '', url.toString())
        }
      } catch (e) {
        // ignore URL update errors
      }
    }).catch(err => {
      console.error('Failed to load P&L data', err)
      this.setState({ rows: [], loading: false })
    })
  }

  // helper to sum by th (group type) or filter predicate
  // sum only non-zero totals
  sumBy(predicate) {
    return this.state.rows
      .filter(predicate)
      .filter(r => Number(r.totamount) && Number(r.totamount) !== 0)
      .reduce((s, r) => s + (Number(r.totamount) || 0), 0)
  }

  render() {
    const { rows, loading } = this.state
    // Common mappings — adjust if your data uses different codes
    // Revenue: th === 'TH1' (income), Expenses: th === 'TH2'
    const revenue = this.sumBy(r => r.th === 'TH1')
    const expenses = this.sumBy(r => r.th === 'TH2')
    const netProfit = revenue - expenses

    return (
      <div>
        <Header />
        <Menu />
        <div className="content-wrapper">
          <div className="card card-info">
            <ol className="breadcrumb float-sm-right">
              <li className="breadcrumb-item text-secondary">
                <Link to="/accounts">Home</Link>
              </li>
              <li className="breadcrumb-item active">Profit & Loss (Schedule III)</li>
            </ol>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="text-center mb-3">Profit & Loss Statement (Schedule III)</h4>
                <div className="btn-group">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => this.exportToExcel()} title="Export to Excel">Export Excel</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => this.exportToPDF()} title="Export to PDF">Export PDF</button>
                </div>
              </div>
              <div>
                <fieldset className="form-group col-md-4 mt-0 mb-3">
                  <Autocomplete
                    id="fylist"
                    className="fy-select"
                    options={this.state.fylist || []}
                    getOptionLabel={(option) => {
                      if (!option) return ''
                      return getFinancialYearLabel(option.from, option.to)
                    }}
                    value={this.state.fySelected || null}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false
                      return String(option.from) === String(value.from) && String(option.to) === String(value.to)
                    }}
                    onChange={(e, value) => {
                      // ensure we store the reference from options when available
                      const chosen = (this.state.fylist || []).find(f => String(f.from) === String((value || {}).from) && String(f.to) === String((value || {}).to)) || value
                      this.setState({ fySelected: chosen });
                      this.onFySelect(value);
                    }}
                    renderInput={(params) => (<TextField {...params} className="form-control mb-3" type="text" name="fylist" label="Select Financial year" variant="standard" fullWidth/>)}
                  />
                </fieldset>
              </div>
              {loading ? <div className="text-center">Loading...</div> : (
                <Table className="table table-sm table-borderless">
                  <TableHead>
                    <TableRow>
                      <TableCell>Particulars</TableCell>
                       <TableCell className="text-right">Amount ({currency || 'INR'})</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-weight-bold">I. Revenue from operations</TableCell>
                      <TableCell />
                    </TableRow>
                    {rows.filter(r => r.th === 'TH1' && Number(r.totamount) && Number(r.totamount) !== 0).map((r, i) => (
                      <TableRow key={`${String(r.id || r.ledid || r.name)}-${i}`}>
                        <TableCell className="pl-4">{r.ledname || r.name}</TableCell>
                        <TableCell className="text-right">
                  <NumericFormat displayType={'text'} thousandSeparator={true} value={Number(r.totamount) || 0} thousandsGroupStyle={thousandsdisplay} decimalScale={2} fixedDecimalScale prefix={currency ? (String(currency) + ' ') : ''} />
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow>
                      <TableCell className="font-weight-bold">Total Revenue</TableCell>
                      <TableCell className="text-right font-weight-bold">
                  <NumericFormat displayType={'text'} thousandSeparator={true} value={revenue} thousandsGroupStyle={thousandsdisplay} decimalScale={2} fixedDecimalScale prefix={currency ? (String(currency) + ' ') : ''} />
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell className="font-weight-bold">II. Expenses</TableCell>
                      <TableCell />
                    </TableRow>
                    {rows.filter(r => r.th === 'TH2' && Number(r.totamount) && Number(r.totamount) !== 0).map((r, i) => (
                      <TableRow key={`${String(r.id || r.ledid || r.name)}-${i}`}>
                        <TableCell className="pl-4">{r.ledname || r.name}</TableCell>
                        <TableCell className="text-right">
                  <NumericFormat displayType={'text'} thousandSeparator={true} value={Number(r.totamount) || 0} thousandsGroupStyle={thousandsdisplay} decimalScale={2} fixedDecimalScale prefix={currency ? (String(currency) + ' ') : ''} />
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow>
                      <TableCell className="font-weight-bold">Total Expenses</TableCell>
                      <TableCell className="text-right font-weight-bold">
                        <NumericFormat displayType={'text'} thousandSeparator={true} value={expenses} thousandsGroupStyle={thousandsdisplay} decimalScale={2} fixedDecimalScale prefix={currency ? (String(currency) + ' ') : ''} />
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell className="font-weight-bold">Net Profit / (Loss)</TableCell>
                      <TableCell className={`text-right font-weight-bold ${netProfit < 0 ? 'text-danger' : 'text-success'}`}>
                        <NumericFormat displayType={'text'} thousandSeparator={true} value={netProfit} thousandsGroupStyle={thousandsdisplay} decimalScale={2} fixedDecimalScale prefix={currency ? (String(currency) + ' ') : ''} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // Export visible rows to Excel using SheetJS
  exportToExcel() {
    try {
      const cur = currency || 'INR'
      const dataRows = (this.state.rows || []).filter(r => Number(r.totamount) && Number(r.totamount) !== 0).map(r => ({ Particulars: r.ledname || r.name, [`Amount (${cur})`]: currency ? (String(currency) + ' ' + (Number(r.totamount) || 0)) : (Number(r.totamount) || 0), AmountRaw: Number(r.totamount) || 0 }))
      const ws = XLSX.utils.json_to_sheet(dataRows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'P&L')
      const fname = `ProfitAndLoss_${this.state.fyfrom || 'FY'}.xlsx`
      XLSX.writeFile(wb, fname)
    } catch (e) {
      // fallback: alert
      console.error('Failed to export Excel', e)
      alert('Export to Excel failed: ' + (e && e.message ? e.message : 'unknown'))
    }
  }

  // Export visible rows to PDF using jsPDF + autotable, include logo when available
  async exportToPDF() {
    try {
      const doc = new jsPDF('p', 'pt', 'a4')
      const marginLeft = 40
      let cursorY = 40
      // attempt to load logo
      try {
        const logoUrl = Credentials && Credentials.getLogo ? Credentials.getLogo() : (Credentials.logo || '')
        if (logoUrl) {
          // fetch image and convert to dataURL
          const res = await fetch(logoUrl)
          const blob = await res.blob()
          const imgData = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
          // add image (fit width ~100)
          doc.addImage(imgData, 'PNG', marginLeft, cursorY, 80, 40)
        }
      } catch (e) {
        // ignore logo errors
      }

      // company name (header)
      const company = Credentials && clientname  ? String(clientname) : ''
      if (company) {
        doc.setFontSize(16)
        doc.text(company, marginLeft, cursorY + 10)
        cursorY += 20
      }

      // title
      doc.setFontSize(14)
      doc.text('Profit & Loss Statement (Schedule III)', marginLeft + (company ? 0 : 120), cursorY + 20)
      cursorY += 30

      // selected financial year on second line (if available)
      const fyLabel = (this.state.fyfrom && this.state.fyto) ? getFinancialYearLabel(this.state.fyfrom, this.state.fyto) : (this.state.fySelected ? getFinancialYearLabel(this.state.fySelected.from, this.state.fySelected.to) : '')
      if (fyLabel) {
        doc.setFontSize(12)
        doc.text(`Financial Year: ${fyLabel}`, marginLeft, cursorY + 10)
        cursorY += 20
      }

      const cur = currency || 'INR'
      const body = (this.state.rows || []).filter(r => Number(r.totamount) && Number(r.totamount) !== 0).map(r => [r.ledname || r.name, currency ? (String(currency) + ' ' + (Number(r.totamount) || 0).toFixed(2)) : (Number(r.totamount) || 0).toFixed(2)])
      // add table
      // @ts-ignore
      doc.autoTable({
        head: [['Particulars', `Amount (${cur})`]],
        body,
        startY: cursorY,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [230, 230, 230] },
        columnStyles: { 1: { halign: 'right' } }
      })

      // totals
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : cursorY + body.length * 10 + 20
      const revenue = this.sumBy(r => r.th === 'TH1')
      const expenses = this.sumBy(r => r.th === 'TH2')
      const netProfit = revenue - expenses
      doc.setFontSize(11)
  const revenueLabel = currency ? (String(currency) + ' ' + revenue.toFixed(2)) : revenue.toFixed(2)
  const expensesLabel = currency ? (String(currency) + ' ' + expenses.toFixed(2)) : expenses.toFixed(2)
  const netLabel = currency ? (String(currency) + ' ' + netProfit.toFixed(2)) : netProfit.toFixed(2)
  doc.text(`Total Revenue (${cur}): ${revenueLabel}`, marginLeft, finalY)
  doc.text(`Total Expenses (${cur}): ${expensesLabel}`, marginLeft, finalY + 14)
  doc.text(`Net Profit / (Loss) (${cur}): ${netLabel}`, marginLeft, finalY + 28)

      const fname = `ProfitAndLoss_${this.state.fyfrom || 'FY'}.pdf`
      doc.save(fname)
    } catch (e) {
      console.error('Failed to export PDF', e)
      alert('Export to PDF failed: ' + (e && e.message ? e.message : 'unknown'))
    }
  }
}

export default withRouter(ProfitAndLossScheduleIII)
